import { useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import { Badge } from "../../../components/ui/badge";
import { SemanticContentView } from "../workdoc/blocks/SemanticContentView";
import { TypedValuePreview } from "../workdoc/blocks/TypedValuePreview";

const INPUT_TYPE_OPTIONS = ["short", "long", "rich", "list", "checklist", "table"];
const CONTENT_STYLE_OPTIONS = ["default", "instruction", "callout", "section"];
const INPUT_STYLE_OPTIONS = ["default", "emphasis", "compact"];

/**
 * Master v5 Prompt 30C Phase 2 — one block row inside the coach's import
 * preview. Renders with the exact same SemanticContentView/
 * TypedValuePreview components the published document uses (§3). Only
 * the allow-listed fields below are ever sent as a PATCH override —
 * everything else here is local UI state until "Save" is pressed.
 */
export function ImportPreviewBlockRow({ block, index, total, onMove, onToggleExcluded, onSaveOverride, testId }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(block.label || "");
  const [helpText, setHelpText] = useState(block.help_text || "");
  const [inputType, setInputType] = useState(block.input_type || "");
  const [style, setStyle] = useState(block.style || "default");
  const isContent = block.kind === "content";
  const styleOptions = isContent ? CONTENT_STYLE_OPTIONS : INPUT_STYLE_OPTIONS;

  const handleSave = () => {
    onSaveOverride(block.import_key, {
      ...(isContent ? {} : { label, help_text: helpText || null, input_type: inputType }),
      style,
    });
    setEditing(false);
  };

  return (
    <div
      className={`border rounded-lg p-3 ${block.excluded ? "border-ink/10 bg-ink/[0.03] opacity-60" : "border-ink/15 bg-white"}`}
      data-testid={testId || `import-preview-block-${block.import_key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex flex-col shrink-0">
            <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="text-ink-soft hover:text-ink disabled:opacity-30" data-testid={`import-preview-block-${block.import_key}-move-up`}>
              <ArrowUp className="h-3 w-3" />
            </button>
            <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="text-ink-soft hover:text-ink disabled:opacity-30" data-testid={`import-preview-block-${block.import_key}-move-down`}>
              <ArrowDown className="h-3 w-3" />
            </button>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]" data-testid={`import-preview-block-${block.import_key}-kind-badge`}>
                {isContent ? "Static content" : block.input_type}
              </Badge>
              <span className="text-[10px] text-ink-soft font-mono" data-testid={`import-preview-block-${block.import_key}-import-key`}>{block.import_key}</span>
              <span className="text-[10px] text-ink-soft">order {block.order}</span>
            </div>
            {!isContent && <p className="text-sm font-medium text-ink mt-1" data-testid={`import-preview-block-${block.import_key}-label-display`}>{block.label}</p>}
            {!isContent && block.help_text && <p className="text-xs text-ink-soft" data-testid={`import-preview-block-${block.import_key}-help-display`}>{block.help_text}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setEditing((v) => !v)} data-testid={`import-preview-block-${block.import_key}-edit-toggle`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onToggleExcluded(block.import_key, !block.excluded)} data-testid={`import-preview-block-${block.import_key}-exclude-toggle`}>
            {block.excluded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 space-y-2 border-t border-ink/10 pt-3" data-testid={`import-preview-block-${block.import_key}-edit-form`}>
          {!isContent && (
            <>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" data-testid={`import-preview-block-${block.import_key}-label-input`} />
              <Textarea value={helpText} onChange={(e) => setHelpText(e.target.value)} placeholder="Help text" rows={2} data-testid={`import-preview-block-${block.import_key}-help-input`} />
              <Select value={inputType} onValueChange={setInputType}>
                <SelectTrigger data-testid={`import-preview-block-${block.import_key}-input-type-select`}><SelectValue /></SelectTrigger>
                <SelectContent>{INPUT_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger data-testid={`import-preview-block-${block.import_key}-style-select`}><SelectValue /></SelectTrigger>
            <SelectContent>{styleOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={handleSave} data-testid={`import-preview-block-${block.import_key}-save-edit`}>Save</Button>
        </div>
      )}

      {!editing && (
        <div className="mt-2" data-testid={`import-preview-block-${block.import_key}-content-preview`}>
          {isContent
            ? <SemanticContentView semanticContent={block.semantic_content} />
            : <TypedValuePreview inputType={block.input_type} value={block.initial_value} />}
        </div>
      )}
    </div>
  );
}
